import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { AlertTriangle, CheckCircle2, FileArchive, FileText, Loader2, Printer, Ruler, Save, Trash2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import {
  buildBadgeLayout, perPage,
  loadCalibrations, saveCalibration, deleteCalibration,
  getActivePrinter, setActivePrinter, getActiveCalibration,
  DEFAULT_CALIBRATION,
  type PageSize, type Orientation, type LayoutMode, type PrinterCalibration,
} from "./AveryTemplates";
import { PrintSheet } from "./PrintSheet";
import { exportBulkPdf, exportZipPng, exportCalibrationPdf, exportPreviewPdf } from "./exporters";
import type { Attendee, Branding } from "./types";

interface Props {
  attendees: Attendee[];
  selectedAttendees: Attendee[];
  branding: Branding | null;
  templateId: string;
  setTemplateId: (id: string) => void;
}

type PrintSource = "all" | "selected" | "category" | "onsite";

export function BulkPanel({ attendees, selectedAttendees, branding }: Props) {
  // ─── Settings ───────────────────────────────────────────────
  const [layoutMode, setLayoutMode] = useState<LayoutMode>("letter");
  const [orientation, setOrientation] = useState<Orientation>("portrait");
  const [gapH, setGapH] = useState(5);
  const [gapV, setGapV] = useState(5);
  const [cutGuides, setCutGuides] = useState(true);
  const [pageBorder, setPageBorder] = useState(false);
  const [pageNumbers, setPageNumbers] = useState(true);
  const [printSource, setPrintSource] = useState<PrintSource>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [blankBadges, setBlankBadges] = useState<number>(0);
  const [onsiteBlankCount, setOnsiteBlankCount] = useState<number>(20);

  // ─── Calibration ────────────────────────────────────────────
  const [calibrations, setCalibrations] = useState<Record<string, PrinterCalibration>>(loadCalibrations());
  const [activePrinter, setActivePrinterState] = useState<string>(getActivePrinter());
  const [calDraft, setCalDraft] = useState<PrinterCalibration>(getActiveCalibration());
  const [newPrinter, setNewPrinter] = useState("");

  useEffect(() => { setCalDraft(calibrations[activePrinter] || { ...DEFAULT_CALIBRATION, printer: activePrinter }); }, [activePrinter, calibrations]);

  // Derive template from live settings
  const pageSize: PageSize = layoutMode === "a4" ? "a4" : "letter";
  const template = useMemo(() => {
    if (layoutMode === "avery") {
      return buildBadgeLayout({ pageSize: "letter", orientation, gapH: 3, gapV: 0, id: "avery-badge-letter", label: "Avery-compatible · Letter" });
    }
    return buildBadgeLayout({ pageSize, orientation, gapH, gapV, id: `${pageSize}-${orientation}-${gapH}x${gapV}` });
  }, [layoutMode, pageSize, orientation, gapH, gapV]);

  // ─── Data pipeline ──────────────────────────────────────────
  const categories = useMemo(() => Array.from(new Set(attendees.map((a) => a.registrationCategory).filter(Boolean))).sort(), [attendees]);

  const source = useMemo(() => {
    // Onsite mode: produce ONLY generic blank badges for walk-in registrants.
    if (printSource === "onsite") {
      const out: Attendee[] = [];
      const n = Math.max(0, Math.floor(onsiteBlankCount) || 0);
      for (let i = 0; i < n; i++) {
        out.push({
          key: `onsite-blank-${i}`,
          registrationId: `onsite-blank-${i}`,
          memberId: null,
          fullName: "",
          region: branding?.region || "Southern",
          city: "",
          registrationCategory: "",
          attendeeType: "guest",
          paymentStatus: "",
          registrationStatus: "",
        } as Attendee);
      }
      return out;
    }

    let base: Attendee[];
    if (printSource === "selected") base = selectedAttendees;
    else if (printSource === "category" && categoryFilter !== "all") base = attendees.filter((a) => a.registrationCategory === categoryFilter);
    else base = attendees;
    // The hook already applies cancellation filtering and safe duplicate
    // cleanup. Bulk print must not run another name-based pass here, because
    // that can drop legitimate attendees who share a name within a family.
    const out: Attendee[] = [...base];
    // Append blank on-site write-in badges at the end of the print run.
    const n = Math.max(0, Math.floor(blankBadges) || 0);
    for (let i = 0; i < n; i++) {
      out.push({
        key: `blank-${i}`,
        registrationId: `blank-${i}`,
        memberId: null,
        fullName: "",
        region: branding?.region || "Southern",
        city: "",
        registrationCategory: "",
        attendeeType: "guest",
        paymentStatus: "",
        registrationStatus: "",
      } as Attendee);
    }
    return out;
  }, [printSource, categoryFilter, attendees, selectedAttendees, blankBadges, onsiteBlankCount, branding?.region]);

  const per = perPage(template);
  const totalPages = Math.max(1, Math.ceil(source.length / per));
  const isShortSelectedRun = printSource === "selected" && selectedAttendees.length > 0 && selectedAttendees.length < attendees.length;
  const [busy, setBusy] = useState<"pdf" | "zip" | "cal" | "preview" | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number; label: string } | null>(null);
  const [statusFeed, setStatusFeed] = useState<{ ts: number; msg: string; kind: "info" | "ok" | "err" }[]>([]);
  const printRef = useRef<HTMLDivElement | null>(null);

  const pushStatus = (msg: string, kind: "info" | "ok" | "err" = "info") => {
    setStatusFeed((prev) => [...prev.slice(-49), { ts: Date.now(), msg, kind }]);
  };
  const resetFeed = (opening: string) => {
    setStatusFeed([{ ts: Date.now(), msg: opening, kind: "info" }]);
  };

  // ─── Exports / print ────────────────────────────────────────
  const doPdf = async () => {
    setBusy("pdf");
    setProgress({ done: 0, total: source.length, label: "Preparing PDF…" });
    resetFeed(`Starting PDF export for ${source.length} badge${source.length === 1 ? "" : "s"} (${totalPages} page${totalPages === 1 ? "" : "s"}, ${template.pageSize.toUpperCase()} ${template.orientation}).`);
    try {
      let last = -1;
      await exportBulkPdf(source, branding, template, (d, t) => {
        setProgress({ done: d, total: t, label: `Rendering badge ${d} of ${t}` });
        // Log every ~10% or on completion so the feed stays readable.
        const pct = Math.floor((d / Math.max(1, t)) * 100);
        if (pct >= last + 10 || d === t) {
          last = pct - (pct % 10);
          pushStatus(`Rendered ${d} / ${t} badges (${pct}%).`);
        }
      }, calDraft);
      pushStatus(`PDF ready · ${source.length} badges · ${totalPages} pages.`, "ok");
      toast({ title: "PDF ready", description: `${source.length} badges · ${totalPages} pages · 100 % scale.` });
    } catch (e: any) {
      pushStatus(`PDF export failed: ${e.message}`, "err");
      toast({ title: "PDF export failed", description: e.message, variant: "destructive" });
    } finally { setBusy(null); setProgress(null); }
  };

  const doZip = async () => {
    setBusy("zip");
    setProgress({ done: 0, total: source.length, label: "Preparing ZIP…" });
    resetFeed(`Starting ZIP export for ${source.length} PNG badge${source.length === 1 ? "" : "s"}.`);
    try {
      let last = -1;
      await exportZipPng(source, branding, template.badgeW * calDraft.scaleX, template.badgeH * calDraft.scaleY, (d, t) => {
        setProgress({ done: d, total: t, label: `Rendering PNG ${d} of ${t}` });
        const pct = Math.floor((d / Math.max(1, t)) * 100);
        if (pct >= last + 10 || d === t) {
          last = pct - (pct % 10);
          pushStatus(`Rendered ${d} / ${t} PNGs (${pct}%).`);
        }
      });
      pushStatus(`ZIP ready · ${source.length} PNG badges.`, "ok");
      toast({ title: "ZIP ready", description: `${source.length} PNG badges.` });
    } catch (e: any) {
      pushStatus(`ZIP export failed: ${e.message}`, "err");
      toast({ title: "ZIP export failed", description: e.message, variant: "destructive" });
    } finally { setBusy(null); setProgress(null); }
  };

  const doPrint = () => {
    const el = printRef.current;
    if (!el) return;
    const w = window.open("", "_blank", "width=900,height=1200");
    if (!w) { toast({ title: "Popup blocked", variant: "destructive" }); return; }
    // @page rules force browser to preserve 100 % scale and skip fit-to-page.
    w.document.write(`<!doctype html><html><head><title>OFUESI Badges — ${template.pageSize.toUpperCase()} ${template.orientation}</title>
      <style>
        @page { size: ${template.pageSize} ${template.orientation}; margin: 0; }
        html, body { margin: 0; padding: 0; background: #fff; font-family: Inter, system-ui, sans-serif; }
        .print-sheet-page { page-break-after: always; break-after: page; box-shadow: none !important; margin: 0 !important; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style></head><body>${el.innerHTML}
      <script>window.onload=()=>{setTimeout(()=>{window.focus();window.print();},250);};</script>
      </body></html>`);
    w.document.close();
  };

  const doPreviewPdf = async () => {
    const el = printRef.current;
    if (!el) { toast({ title: "Preview not ready", variant: "destructive" }); return; }
    const scaledWrapper = el.parentElement as HTMLElement | null;
    const prevTransform = scaledWrapper?.style.transform ?? "";
    if (scaledWrapper) scaledWrapper.style.transform = "none";
    setBusy("preview");
    setProgress({ done: 0, total: totalPages, label: "Capturing preview…" });
    resetFeed(`Capturing ${totalPages} preview page${totalPages === 1 ? "" : "s"} from the on-screen sheet.`);
    try {
      await exportPreviewPdf(el, pageSize, orientation, (d, t) => {
        setProgress({ done: d, total: t, label: `Capturing page ${d} of ${t}` });
        pushStatus(`Captured page ${d} / ${t}.`);
      });
      pushStatus(`Preview PDF ready · ${totalPages} page${totalPages === 1 ? "" : "s"}.`, "ok");
      toast({ title: "Preview PDF ready", description: `${totalPages} page${totalPages === 1 ? "" : "s"} captured from the on-screen preview.` });
    } catch (e: any) {
      pushStatus(`Preview PDF failed: ${e.message}`, "err");
      toast({ title: "Preview PDF failed", description: e.message, variant: "destructive" });
    } finally {
      if (scaledWrapper) scaledWrapper.style.transform = prevTransform;
      setBusy(null); setProgress(null);
    }
  };

  const doCalibrationPage = async () => {
    setBusy("cal");
    try {
      await exportCalibrationPdf(pageSize);
      toast({ title: "Calibration page ready", description: "Print at 100 % scale, then measure with a ruler." });
    } catch (e: any) {
      toast({ title: "Calibration export failed", description: e.message, variant: "destructive" });
    } finally { setBusy(null); }
  };

  const saveActiveCalibration = () => {
    saveCalibration(calDraft);
    setCalibrations(loadCalibrations());
    toast({ title: "Calibration saved", description: `Profile "${calDraft.printer}" saved.` });
  };
  const addNewPrinter = () => {
    const name = newPrinter.trim();
    if (!name) return;
    const c: PrinterCalibration = { ...DEFAULT_CALIBRATION, printer: name, updatedAt: Date.now() };
    saveCalibration(c); setCalibrations(loadCalibrations());
    setActivePrinter(name); setActivePrinterState(name);
    setNewPrinter("");
  };
  const removePrinter = () => {
    if (activePrinter === "Default") return;
    deleteCalibration(activePrinter);
    setCalibrations(loadCalibrations());
    setActivePrinter("Default"); setActivePrinterState("Default");
  };
  const changeActivePrinter = (name: string) => { setActivePrinter(name); setActivePrinterState(name); };

  const nudge = (field: keyof PrinterCalibration, delta: number) => {
    setCalDraft((c) => ({ ...c, [field]: (c[field] as number) + delta }));
  };

  return (
    <div className="space-y-6">
      {/* ── Settings ── */}
      <Card>
        <CardHeader><CardTitle>Badge print settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Badge size</Label>
              <div className="text-sm font-medium mt-2">60 × 90 mm (fixed)</div>
              <div className="text-[11px] text-muted-foreground">Matches purchased holders</div>
            </div>
            <div>
              <Label>Page size</Label>
              <Select value={layoutMode} onValueChange={(v) => setLayoutMode(v as LayoutMode)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="letter">US Letter (8.5 × 11″)</SelectItem>
                  <SelectItem value="a4">A4 (210 × 297 mm)</SelectItem>
                  <SelectItem value="avery">Avery-compatible layout</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Orientation</Label>
              <Select value={orientation} onValueChange={(v) => setOrientation(v as Orientation)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait">Portrait</SelectItem>
                  <SelectItem value="landscape">Landscape</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scale</Label>
              <div className="text-sm font-medium mt-2">100 % (locked)</div>
              <div className="text-[11px] text-muted-foreground">Never scaled at print time</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Horizontal gap (mm)</Label>
              <Input type="number" min={0} step={1} value={gapH} onChange={(e) => setGapH(Math.max(0, Number(e.target.value) || 0))} disabled={layoutMode === "avery"} />
            </div>
            <div>
              <Label>Vertical gap (mm)</Label>
              <Input type="number" min={0} step={1} value={gapV} onChange={(e) => setGapV(Math.max(0, Number(e.target.value) || 0))} disabled={layoutMode === "avery"} />
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setGapH(0); setGapV(0); }} disabled={layoutMode === "avery"}>No gap</Button>
              <Button variant="outline" size="sm" onClick={() => { setGapH(5); setGapV(5); }} disabled={layoutMode === "avery"}>5 mm</Button>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2"><Switch checked={cutGuides} onCheckedChange={setCutGuides} /><Label>Show cut lines</Label></div>
              <div className="flex items-center gap-2"><Switch checked={pageBorder} onCheckedChange={setPageBorder} /><Label>Show page border</Label></div>
              <div className="flex items-center gap-2"><Switch checked={pageNumbers} onCheckedChange={setPageNumbers} /><Label>Page numbers</Label></div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label>Print source</Label>
              <Select value={printSource} onValueChange={(v) => setPrintSource(v as PrintSource)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All attendees ({attendees.length})</SelectItem>
                  <SelectItem value="selected" disabled={selectedAttendees.length === 0}>Selected only ({selectedAttendees.length})</SelectItem>
                  <SelectItem value="category">By category…</SelectItem>
                  <SelectItem value="onsite">Onsite registration badges (generic blanks)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {printSource === "category" && (
              <div>
                <Label>Category</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {printSource !== "onsite" && (
              <div>
                <Label>Blank badges (on-site write-in)</Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={blankBadges}
                  onChange={(e) => setBlankBadges(Math.max(0, Number(e.target.value) || 0))}
                  placeholder="0"
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  Appends this many blank name badges (no category) to the run.
                </div>
              </div>
            )}
          </div>

          {printSource === "onsite" && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Onsite registration badges</div>
                <div className="text-[12px] text-muted-foreground">
                  Generic blank name badges with no category printed. Hand these out to walk-in registrants or anyone who needs a replacement badge.
                </div>
              </div>
              <div className="max-w-xs">
                <Label>Number of generic onsite badges</Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={onsiteBlankCount}
                  onChange={(e) => setOnsiteBlankCount(Math.max(0, Number(e.target.value) || 0))}
                />
              </div>
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            {source.length} badge{source.length === 1 ? "" : "s"}{printSource !== "onsite" && blankBadges > 0 ? ` (${blankBadges} blank)` : ""}{printSource === "onsite" ? " (onsite blanks)" : ""} · live roster: {attendees.length} attendee{attendees.length === 1 ? "" : "s"} · {template.cols}×{template.rows} per page · {totalPages} page{totalPages === 1 ? "" : "s"} · {template.pageSize.toUpperCase()} {template.orientation} · calibration: {calDraft.printer}
          </div>

          {isShortSelectedRun && (
            <div className="flex flex-col gap-3 rounded-md border bg-muted/50 p-3 text-sm text-foreground md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  Bulk print is currently set to selected attendees only ({source.length}), so {attendees.length - source.length} live attendee{attendees.length - source.length === 1 ? "" : "s"} will not print.
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setPrintSource("all")}>
                Use all {attendees.length} attendees
              </Button>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={doPdf} disabled={busy !== null || source.length === 0}>
              {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Download PDF
            </Button>
            <Button variant="outline" onClick={doZip} disabled={busy !== null || source.length === 0}>
              {busy === "zip" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileArchive className="w-4 h-4 mr-2" />}
              Download ZIP (PNG)
            </Button>
            <Button variant="secondary" onClick={doPrint} disabled={source.length === 0}>
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button variant="outline" onClick={doPreviewPdf} disabled={busy !== null || source.length === 0}>
              {busy === "preview" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Download preview as PDF
            </Button>
          </div>
          {(progress || statusFeed.length > 0) && (
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
              {progress && (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <div className="font-medium text-foreground">{progress.label}</div>
                    <div className="tabular-nums text-muted-foreground">
                      {progress.done} / {progress.total} ·{" "}
                      {Math.floor((progress.done / Math.max(1, progress.total)) * 100)}%
                    </div>
                  </div>
                  <Progress value={(progress.done / Math.max(1, progress.total)) * 100} />
                </>
              )}
              {statusFeed.length > 0 && (
                <div className="max-h-40 overflow-auto rounded border bg-background/60 p-2 text-xs font-mono space-y-1">
                  {statusFeed.map((s, i) => (
                    <div
                      key={i}
                      className={
                        s.kind === "err"
                          ? "text-destructive"
                          : s.kind === "ok"
                            ? "text-emerald-600"
                            : "text-muted-foreground"
                      }
                    >
                      <span className="opacity-60 mr-2">
                        {new Date(s.ts).toLocaleTimeString([], { hour12: false })}
                      </span>
                      {s.kind === "ok" && <CheckCircle2 className="inline w-3 h-3 mr-1 -mt-0.5" />}
                      {s.msg}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Calibration ── */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Ruler className="w-4 h-4" /> Print calibration</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Print the calibration page once per printer, measure the 60 × 90 mm rectangle,
            then nudge the size and offset until it fits the purchased holder exactly.
            Settings are saved per printer profile.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Active printer profile</Label>
              <Select value={activePrinter} onValueChange={changeActivePrinter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(calibrations).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Add printer (HP, Brother, Canon…)</Label>
              <div className="flex gap-2">
                <Input value={newPrinter} onChange={(e) => setNewPrinter(e.target.value)} placeholder="Printer label" />
                <Button variant="outline" onClick={addNewPrinter} disabled={!newPrinter.trim()}>Add</Button>
              </div>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={doCalibrationPage} disabled={busy !== null}>
                {busy === "cal" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Download calibration page
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <CalNudge label="Width scale" suffix="%" value={calDraft.scaleX} display={(v) => `${(v * 100).toFixed(1)}`} onDec={() => nudge("scaleX", -0.002)} onInc={() => nudge("scaleX", 0.002)} />
            <CalNudge label="Height scale" suffix="%" value={calDraft.scaleY} display={(v) => `${(v * 100).toFixed(1)}`} onDec={() => nudge("scaleY", -0.002)} onInc={() => nudge("scaleY", 0.002)} />
            <CalNudge label="Offset X" suffix="mm" value={calDraft.offsetX} display={(v) => v.toFixed(1)} onDec={() => nudge("offsetX", -0.5)} onInc={() => nudge("offsetX", 0.5)} />
            <CalNudge label="Offset Y" suffix="mm" value={calDraft.offsetY} display={(v) => v.toFixed(1)} onDec={() => nudge("offsetY", -0.5)} onInc={() => nudge("offsetY", 0.5)} />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setCalDraft({ ...DEFAULT_CALIBRATION, printer: activePrinter, updatedAt: Date.now() })}>Perfect fit (reset)</Button>
            <Button onClick={saveActiveCalibration}><Save className="w-4 h-4 mr-2" /> Save calibration</Button>
            <Button variant="ghost" onClick={removePrinter} disabled={activePrinter === "Default"} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" /> Remove profile</Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Preview ── */}
      <Card>
        <CardHeader><CardTitle>Print preview</CardTitle></CardHeader>
        <CardContent>
          <div className="bg-muted/30 rounded-lg p-4 overflow-auto max-h-[900px]">
            <div style={{ transform: "scale(0.55)", transformOrigin: "top left" }}>
              <PrintSheet
                ref={printRef}
                template={template}
                attendees={source}
                branding={branding}
                dpi={100}
                showCutGuides={cutGuides}
                showPageNumbers={pageNumbers}
                showPageBorder={pageBorder}
                calibration={calDraft}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CalNudge({ label, suffix, value, display, onDec, onInc }: {
  label: string; suffix: string; value: number; display: (v: number) => string; onDec: () => void; onInc: () => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <Button variant="outline" size="sm" onClick={onDec}>−</Button>
        <div className="flex-1 text-center text-sm font-mono tabular-nums">{display(value)} {suffix}</div>
        <Button variant="outline" size="sm" onClick={onInc}>+</Button>
      </div>
    </div>
  );
}
